import {Show, Text, VStack} from 'platform/foundation';
import type Toolbar from 'quill/modules/toolbar';

import {
  ComponentProps,
  ForwardedRef,
  MouseEvent,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import {isNotNil} from 'ramda-adjunct';

import {suffixTestId} from 'shared/web';

import {HelperText} from '../HelperText/HelperText';
import {Label} from '../Label/Label';
import {useTranslationContext} from '../TranslationProvider/TranslationContext';
import {
  EmojiButton,
  EmojiCategoryButton,
  EmojiCategoryTabs,
  EmojiGrid,
  EmojiPanel,
  EmojiPickerWrapper,
} from './components/EmojiPicker';
import {FieldWrapper} from './components/FieldWrapper';
import {FooterBar, FooterCounter} from './components/FooterBar';
import {StyledQuillWrapper} from './components/QuillWrapper';
import {useRichTextEditor} from './hooks/useRichTextEditor';
import {RichTextProps, ToolbarOption} from './types';
import {buildCounterLabel} from './utils/buildCounterLabel';

const EMOJI_DATA = [
  {key: 'smileys', emojis: ['😀', '😄', '😉', '😊', '😍', '🤝']},
  {key: 'nature', emojis: ['🌞', '🌿', '🌲', '🌷', '🍀', '🔥']},
  {key: 'transport', emojis: ['🚗', '🚙', '🛻', '🏎️', '🛞', '⛽']},
  {key: 'symbols', emojis: ['⭐', '✅', '⚠️', '💡', '📌', '🛠️']},
] as const;

const ALL_TOOLBAR_OPTIONS: ToolbarOption[] = [
  'bold',
  'italic',
  'underline',
  'strike',
  'header',
  'blockquote',
  'listOrdered',
  'listBullet',
  'align',
  'clean',
  'emoji',
];

// Maps each toolbar option to its underlying Quill format name.
// Options without a Quill format (clean, emoji) are absent.
const TOOLBAR_OPTION_TO_FORMAT: Partial<Record<ToolbarOption, string>> = {
  bold: 'bold',
  italic: 'italic',
  underline: 'underline',
  strike: 'strike',
  header: 'header',
  blockquote: 'blockquote',
  listOrdered: 'list',
  listBullet: 'list',
  align: 'align',
};

type QuillEditor = ReturnType<ReactQuill['getEditor']>;
type ToolbarModule = Toolbar & {
  __richTextSkipsNullRange?: boolean;
};

function patchToolbarNullRangeUpdate(editor: QuillEditor) {
  const toolbar = editor.getModule('toolbar') as ToolbarModule | null;

  if (!toolbar || toolbar.__richTextSkipsNullRange) {
    return;
  }

  const updateToolbar = toolbar.update.bind(toolbar);

  toolbar.update = (range) => {
    if (!range) {
      return;
    }

    updateToolbar(range);
  };
  toolbar.__richTextSkipsNullRange = true;
}

function handleToolbarMouseDown(event: MouseEvent<HTMLDivElement>) {
  const target = event.target;
  if (!(target instanceof HTMLElement)) {
    return;
  }

  const button = target.closest('button');
  if (button && event.currentTarget.contains(button)) {
    event.preventDefault();
  }
}

export const RichText = forwardRef(
  (props: RichTextProps, userRef: ForwardedRef<HTMLDivElement>) => {
    const t = useTranslationContext();
    const quillRef = useRef<ReactQuill>(null);

    const {
      id,
      helperId,
      toolbarId,
      isFocused,
      currentLength,
      isOverLimit,
      isNearLimit,
      handleFocus,
      handleBlur,
      onChange,
    } = useRichTextEditor(props, userRef);

    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
    const [activeEmojiCategory, setActiveEmojiCategory] = useState(0);
    const [selectionPathKey, setSelectionPathKey] = useState('richText.formats.paragraph');

    const enabledOptions = props.toolbarOptions ?? ALL_TOOLBAR_OPTIONS;
    const enabledOptionSet = new Set(enabledOptions);

    // Callers commonly pass a fresh array literal each render. Deriving Quill's `formats`
    // prop from a content-based string key keeps its identity stable — without this, Quill
    // re-instantiates every render and stacks duplicate native click listeners on toolbar
    // buttons, so clicks fire N times and cancel out for even N (bold appears stuck, etc.).
    const quillFormatsKey = enabledOptions
      .map((option) => TOOLBAR_OPTION_TO_FORMAT[option])
      .filter((format): format is string => isNotNil(format))
      .join('|');

    const quillFormats = useMemo(
      () => (quillFormatsKey ? Array.from(new Set(quillFormatsKey.split('|'))) : []),
      [quillFormatsKey]
    );

    const emojiCategories = useMemo(
      () =>
        EMOJI_DATA.map((category) => ({
          label: t(`richText.emojiCategories.${category.key}`),
          emojis: category.emojis,
        })),
      [t]
    );

    const quillValue = props.value ?? '';

    const handleChange: ComponentProps<typeof ReactQuill>['onChange'] = (value, _delta, source) => {
      if (source !== 'user') {
        return;
      }
      onChange?.(value);
    };

    const handleQuillRef = useCallback((instance: ReactQuill | null) => {
      quillRef.current = instance;

      if (!instance) {
        return;
      }

      patchToolbarNullRangeUpdate(instance.getEditor());
    }, []);

    // Quill requires referentially stable modules object — useMemo is necessary here.
    const modules = useMemo(
      () => ({
        toolbar: {
          container: `#${toolbarId}`,
        },
      }),
      [toolbarId]
    );

    const handleSelectionChange = useCallback(() => {
      const editor = quillRef.current?.getEditor();
      if (!editor) {
        return;
      }
      const range = editor.getSelection();
      if (!range) {
        return;
      }
      const formats = editor.getFormat(range);
      if (formats.header === 1) {
        setSelectionPathKey('richText.formats.heading1');
      } else if (formats.header === 2) {
        setSelectionPathKey('richText.formats.heading2');
      } else if (formats.header === 3) {
        setSelectionPathKey('richText.formats.heading3');
      } else if (formats.blockquote) {
        setSelectionPathKey('richText.formats.quote');
      } else if (formats.list === 'ordered') {
        setSelectionPathKey('richText.toolbar.numberedList');
      } else if (formats.list === 'bullet') {
        setSelectionPathKey('richText.toolbar.bulletedList');
      } else {
        setSelectionPathKey('richText.formats.paragraph');
      }
    }, []);

    const handleEmojiInsert = useCallback((emoji: string) => {
      const editor = quillRef.current?.getEditor();
      if (editor) {
        const selection = editor.getSelection();
        const index = selection ? selection.index : editor.getLength();
        editor.insertText(index, emoji, 'user');
        editor.setSelection(index + emoji.length);
      }
      setIsEmojiPickerOpen(false);
    }, []);

    return (
      <div onFocus={props.onFocus} onBlur={props.onBlur} onKeyDown={props.onKeyDown}>
        <VStack>
          <Label
            id={id}
            isRequired={props.isRequired}
            tooltip={props.tooltip}
            isRecommended={props.isRecommended}
            data-testid={suffixTestId('label', props)}
            actions={props.labelActions}
            maxLength={props.maxLength}
            isCounterVisible={props.isCounterVisible}
            currentLength={currentLength}
          >
            {props.label}
          </Label>
          <FieldWrapper
            $isDisabled={props.isDisabled}
            $isInvalid={props.isInvalid || !!props.errorMessage}
          >
            <StyledQuillWrapper
              $isFocused={isFocused}
              $isDisabled={props.isDisabled}
              $isInvalid={props.isInvalid || !!props.errorMessage}
              $isScrollable={props.isScrollable}
              $rows={props.rows}
              $minRows={props.minRows}
              $maxRows={props.maxRows}
            >
              {/* Quill requires these specific class names to bind toolbar buttons. */}
              {/* eslint-disable react/forbid-dom-props */}
              <div
                id={toolbarId}
                role="toolbar"
                aria-label={t('richText.toolbar.formattingToolbar')}
                onMouseDown={handleToolbarMouseDown}
              >
                <Show
                  when={
                    enabledOptionSet.has('bold') ||
                    enabledOptionSet.has('italic') ||
                    enabledOptionSet.has('underline') ||
                    enabledOptionSet.has('strike')
                  }
                >
                  <span className="ql-formats">
                    <Show when={enabledOptionSet.has('bold')}>
                      <button
                        className="ql-bold"
                        type="button"
                        aria-label={t('richText.toolbar.bold')}
                      />
                    </Show>
                    <Show when={enabledOptionSet.has('italic')}>
                      <button
                        className="ql-italic"
                        type="button"
                        aria-label={t('richText.toolbar.italic')}
                      />
                    </Show>
                    <Show when={enabledOptionSet.has('underline')}>
                      <button
                        className="ql-underline"
                        type="button"
                        aria-label={t('richText.toolbar.underline')}
                      />
                    </Show>
                    <Show when={enabledOptionSet.has('strike')}>
                      <button
                        className="ql-strike"
                        type="button"
                        aria-label={t('richText.toolbar.strikethrough')}
                      />
                    </Show>
                  </span>
                </Show>
                <Show when={enabledOptionSet.has('header') || enabledOptionSet.has('blockquote')}>
                  <span className="ql-formats">
                    <Show when={enabledOptionSet.has('header')}>
                      <select
                        className="ql-header"
                        defaultValue=""
                        aria-label={t('richText.toolbar.heading')}
                      >
                        <option value="1">{t('richText.formats.heading1')}</option>
                        <option value="2">{t('richText.formats.heading2')}</option>
                        <option value="3">{t('richText.formats.heading3')}</option>
                        <option value="">{t('richText.formats.paragraph')}</option>
                      </select>
                    </Show>
                    <Show when={enabledOptionSet.has('blockquote')}>
                      <button
                        className="ql-blockquote"
                        type="button"
                        aria-label={t('richText.toolbar.blockquote')}
                      />
                    </Show>
                  </span>
                </Show>
                <Show
                  when={
                    enabledOptionSet.has('listOrdered') ||
                    enabledOptionSet.has('listBullet') ||
                    enabledOptionSet.has('align')
                  }
                >
                  <span className="ql-formats">
                    <Show when={enabledOptionSet.has('listOrdered')}>
                      <button
                        className="ql-list"
                        value="ordered"
                        type="button"
                        aria-label={t('richText.toolbar.numberedList')}
                      />
                    </Show>
                    <Show when={enabledOptionSet.has('listBullet')}>
                      <button
                        className="ql-list"
                        value="bullet"
                        type="button"
                        aria-label={t('richText.toolbar.bulletedList')}
                      />
                    </Show>
                    <Show when={enabledOptionSet.has('align')}>
                      <button
                        className="ql-align"
                        value=""
                        type="button"
                        aria-label={t('richText.toolbar.alignLeft')}
                      />
                      <button
                        className="ql-align"
                        value="center"
                        type="button"
                        aria-label={t('richText.toolbar.alignCenter')}
                      />
                      <button
                        className="ql-align"
                        value="right"
                        type="button"
                        aria-label={t('richText.toolbar.alignRight')}
                      />
                      <button
                        className="ql-align"
                        value="justify"
                        type="button"
                        aria-label={t('richText.toolbar.justify')}
                      />
                    </Show>
                  </span>
                </Show>
                <Show when={enabledOptionSet.has('clean') || enabledOptionSet.has('emoji')}>
                  <span className="ql-formats">
                    <Show when={enabledOptionSet.has('clean')}>
                      <button
                        className="ql-clean"
                        type="button"
                        aria-label={t('richText.toolbar.clearFormatting')}
                      />
                    </Show>
                    <Show when={enabledOptionSet.has('emoji')}>
                      <EmojiPickerWrapper>
                        <button
                          className="ql-emoji"
                          type="button"
                          aria-label={t('richText.toolbar.insertEmoji')}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setIsEmojiPickerOpen((v) => !v)}
                        >
                          😀
                        </button>
                        <Show when={isEmojiPickerOpen && !props.isDisabled}>
                          <EmojiPanel data-testid={suffixTestId('emojiPicker', props)}>
                            <EmojiCategoryTabs>
                              {emojiCategories.map((category, categoryIndex) => (
                                <EmojiCategoryButton
                                  key={category.label}
                                  role="button"
                                  tabIndex={0}
                                  $isActive={categoryIndex === activeEmojiCategory}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => setActiveEmojiCategory(categoryIndex)}
                                >
                                  {category.label}
                                </EmojiCategoryButton>
                              ))}
                            </EmojiCategoryTabs>
                            <EmojiGrid>
                              {emojiCategories[activeEmojiCategory].emojis.map((emoji) => (
                                <EmojiButton
                                  key={emoji}
                                  role="button"
                                  tabIndex={0}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => handleEmojiInsert(emoji)}
                                >
                                  {emoji}
                                </EmojiButton>
                              ))}
                            </EmojiGrid>
                          </EmojiPanel>
                        </Show>
                      </EmojiPickerWrapper>
                    </Show>
                  </span>
                </Show>
              </div>
              {/* eslint-enable react/forbid-dom-props */}
              <ReactQuill
                ref={handleQuillRef}
                theme="snow"
                value={quillValue}
                onChange={handleChange}
                readOnly={props.isDisabled}
                placeholder={props.placeholder ?? ''}
                modules={modules}
                formats={quillFormats}
                useSemanticHTML={false}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChangeSelection={handleSelectionChange}
                aria-labelledby={id}
                aria-describedby={props.errorMessage || props.helperText ? helperId : undefined}
                aria-invalid={props.isInvalid || !!props.errorMessage || undefined}
              />
            </StyledQuillWrapper>
            <FooterBar>
              <Text size="xSmall" color="secondary">
                {t(selectionPathKey)}
              </Text>
              <Show when={props.isCounterVisible !== false}>
                <FooterCounter $isOverLimit={isOverLimit} $isNearLimit={isNearLimit}>
                  {buildCounterLabel(currentLength, props.maxLength)}
                </FooterCounter>
              </Show>
            </FooterBar>
          </FieldWrapper>
          <Show when={props.errorMessage || props.helperText}>
            <div id={helperId}>
              <HelperText
                errorMessage={props.errorMessage}
                helperText={props.helperText}
                data-testid={suffixTestId('helper', props)}
              />
            </div>
          </Show>
        </VStack>
      </div>
    );
  }
);
